import React from 'react'
import { Input, Modal, message } from 'antd'
import { formatGbp } from './formatGbp'
import { stockInStockWasEntered } from './stockOnHand'

const expectedQtyForLockReview = product => {
  const lastQty = Number(product?.lastQuantity) || 0
  const received = Number(product?.qtySinceLastStockTake) || 0
  const sold = Number(product?.sold) || 0
  const wastage = Number(product?.wastage) || 0
  return lastQty + received - sold - wastage
}

export const buildReviewBeforeLockStats = (stockTake, products = []) => {
  const safeProducts = Array.isArray(products) ? products : []
  const missingCounts = safeProducts.filter(
    product => !stockInStockWasEntered(product),
  ).length
  const topVariances = safeProducts
    .filter(stockInStockWasEntered)
    .map(product => {
      const expectedQty = expectedQtyForLockReview(product)
      const inStock = Number(product?.inStock) || 0
      return {
        title: product?.title || 'Untitled',
        variance: inStock - expectedQty,
      }
    })
    .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance))
    .slice(0, 5)

  return {
    totalStockValue: Number(stockTake?.totalStockValue) || 0,
    totalWastageValue: Number(stockTake?.totalWastageValue) || 0,
    missingCounts,
    topVariances,
  }
}

const LockReviewBody = ({ stats }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <strong>Total stock value:</strong>
      <span>{formatGbp(stats.totalStockValue)}</span>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <strong>Total wastage value:</strong>
      <span>{formatGbp(stats.totalWastageValue)}</span>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <strong>Missing counts:</strong>
      <span>{stats.missingCounts}</span>
    </div>
    <div
      style={{ marginTop: 6, fontSize: 13, color: '#595959', fontWeight: 700 }}
    >
      Top 5 variances
    </div>
    {stats.topVariances.length > 0 ? (
      stats.topVariances.map(item => (
        <div
          key={`${item.title}-${item.variance}`}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 13,
            gap: 12,
          }}
        >
          <span>{item.title}</span>
          <strong>{item.variance.toFixed(2)}</strong>
        </div>
      ))
    ) : (
      <div style={{ color: '#8c8c8c', fontSize: 13 }}>
        No variance data available.
      </div>
    )}
  </div>
)

export const confirmStockTakeLockChange = ({
  stockTake,
  stats,
  venueId,
  venue,
  canUnlockStockTake,
  showVarianceEmail,
  dispatch,
  updateStockTake,
}) => {
  if (!stockTake?._id) return

  const shouldLock = !stockTake.isLocked
  if (shouldLock) {
    const reviewStats = stats || buildReviewBeforeLockStats(stockTake, [])
    Modal.confirm({
      title: 'Review before lock',
      width: 620,
      okText: 'Lock stock take',
      cancelText: 'Cancel',
      content: <LockReviewBody stats={reviewStats} />,
      onOk: () =>
        Promise.resolve(
          dispatch(
            updateStockTake(
              venueId,
              {
                ...stockTake,
                isLocked: true,
              },
              'LOCK',
            ),
          ),
        ).then(() => {
          if (
            showVarianceEmail &&
            venue?.stockTakeAutoEmailEnabled &&
            Array.isArray(venue?.stockTakeReportEmails) &&
            venue.stockTakeReportEmails.length
          ) {
            message.success(
              `Stock take locked. Variance email queued to ${venue.stockTakeReportEmails.length} recipient(s).`,
            )
          } else {
            message.success('Stock take locked.')
          }
          return true
        }),
    })
    return
  }

  if (!canUnlockStockTake) {
    Modal.warning({
      title: 'Unlock not allowed',
      content: 'You do not have permission to unlock a locked stock take.',
      okText: 'OK',
    })
    return
  }

  let unlockReason = ''
  Modal.confirm({
    title: 'Unlock stock take?',
    okText: 'Unlock stock take',
    cancelText: 'Cancel',
    content: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ color: '#8c8c8c', fontSize: 13 }}>
          This stock take is currently locked. Unlocking it may affect reports
          and audit history. Please enter a reason before continuing.
        </div>
        <div
          style={{
            marginTop: 12,
            fontSize: 13,
            color: '#595959',
            fontWeight: 700,
          }}
        >
          Unlock reason
        </div>
        <Input.TextArea
          rows={3}
          placeholder='Enter unlock reason'
          onChange={event => {
            unlockReason = event.target.value
          }}
        />
      </div>
    ),
    onOk: () => {
      const trimmedReason = String(unlockReason || '').trim()
      if (!trimmedReason) {
        return Promise.reject(
          new Error('Unlock reason is required before unlocking.'),
        )
      }
      dispatch(
        updateStockTake(
          venueId,
          {
            _id: stockTake._id,
            isLocked: false,
            unlockReason: trimmedReason,
          },
          'UNLOCK',
        ),
      )
      return Promise.resolve()
    },
  })
}
